/**
 * QuestionEditor Component
 * Apple-quality design with true black dark mode
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { QUESTION_TYPES, AI_CONTEXTS, AI_WEIGHTS, getQuestionType } from './QuestionTypes';

const QuestionEditor = ({ question, onChange, onDelete, isOpen, onToggle }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const questionType = getQuestionType(question.type);

  const handleChange = (field, value) => {
    onChange({ ...question, [field]: value });
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...(question.options || [])];
    newOptions[index] = { ...newOptions[index], [field]: value };
    onChange({ ...question, options: newOptions });
  };

  const addOption = () => {
    const newOptions = [...(question.options || []), { value: '', label: '', score: 50 }];
    onChange({ ...question, options: newOptions });
  };

  const removeOption = (index) => {
    const newOptions = question.options.filter((_, i) => i !== index);
    onChange({ ...question, options: newOptions });
  };

  return (
    <motion.div
      layout
      className="bg-white dark:bg-white/5 rounded-2xl border border-black/5 dark:border-white/10 overflow-hidden"
    >
      {/* Header */}
      <div
        onClick={onToggle}
        className="flex items-center justify-between p-4 cursor-pointer
          hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center">
            {questionType?.icon && <questionType.icon className="w-4 h-4 text-brand" />}
          </div>
          <div>
            <p className="text-[15px] font-medium text-black dark:text-white">
              {question.label || 'Untitled Question'}
            </p>
            <p className="text-[12px] text-black/40 dark:text-white/40">
              {questionType?.label} {question.required && '• Required'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded-lg text-black/30 dark:text-white/30 hover:text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
          <ChevronDownIcon className={`w-5 h-5 text-black/30 dark:text-white/30 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4 border-t border-black/5 dark:border-white/10 pt-4">
              {/* Question Label */}
              <div>
                <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                  Question
                </label>
                <input
                  type="text"
                  value={question.label || ''}
                  onChange={(e) => handleChange('label', e.target.value)}
                  placeholder="e.g., What's your name?"
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                    border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                    placeholder:text-black/30 dark:placeholder:text-white/30
                    focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50 transition-shadow"
                />
              </div>

              {/* Placeholder */}
              {['text', 'email', 'phone', 'textarea'].includes(question.type) && (
                <div>
                  <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                    Placeholder
                  </label>
                  <input
                    type="text"
                    value={question.placeholder || ''}
                    onChange={(e) => handleChange('placeholder', e.target.value)}
                    placeholder={questionType?.defaultPlaceholder}
                    className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                      border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                      placeholder:text-black/30 dark:placeholder:text-white/30
                      focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50 transition-shadow"
                  />
                </div>
              )}

              {/* Options */}
              {questionType?.hasOptions && (
                <div>
                  <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                    Options
                  </label>
                  <div className="space-y-2">
                    {(question.options || []).map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={option.label}
                          onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                          placeholder="Option"
                          className="flex-1 px-3 py-2 rounded-lg bg-black/[0.03] dark:bg-white/5
                            border border-black/5 dark:border-white/10 text-black dark:text-white text-[14px]
                            placeholder:text-black/30 dark:placeholder:text-white/30
                            focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
                        />
                        <input
                          type="number"
                          value={option.score}
                          onChange={(e) => handleOptionChange(index, 'score', parseInt(e.target.value) || 0)}
                          placeholder="Score"
                          min="0"
                          max="100"
                          className="w-16 px-2 py-2 rounded-lg bg-black/[0.03] dark:bg-white/5
                            border border-black/5 dark:border-white/10 text-black dark:text-white text-[14px] text-center
                            focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
                        />
                        <button
                          onClick={() => removeOption(index)}
                          className="p-2 rounded-lg text-black/30 dark:text-white/30 hover:text-red-500 hover:bg-red-500/10"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addOption}
                      className="flex items-center gap-1.5 px-3 py-2 text-[13px] text-brand font-medium
                        hover:bg-brand/10 rounded-lg transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add option
                    </button>
                  </div>
                </div>
              )}

              {/* Rating Scale */}
              {question.type === 'rating' && (
                <div>
                  <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                    Scale
                  </label>
                  <select
                    value={question.scale || 5}
                    onChange={(e) => handleChange('scale', parseInt(e.target.value))}
                    className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                      border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                      focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
                  >
                    <option value={5}>1 to 5</option>
                    <option value={10}>1 to 10</option>
                  </select>
                </div>
              )}

              {/* Required Toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-[14px] font-medium text-black dark:text-white">Required</p>
                  <p className="text-[12px] text-black/40 dark:text-white/40">Must be answered</p>
                </div>
                <button
                  onClick={() => handleChange('required', !question.required)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    question.required ? 'bg-brand' : 'bg-black/10 dark:bg-white/10'
                  }`}
                >
                  <motion.div
                    animate={{ x: question.required ? 20 : 2 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
                  />
                </button>
              </div>

              {/* Advanced */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-1.5 text-[13px] text-black/40 dark:text-white/40
                  hover:text-black/60 dark:hover:text-white/60"
              >
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                Advanced
              </button>

              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="space-y-4 overflow-hidden"
                  >
                    <div>
                      <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                        Data Type
                      </label>
                      <select
                        value={question.aiContext || 'general'}
                        onChange={(e) => handleChange('aiContext', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                          border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                          focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
                      >
                        {Object.entries(AI_CONTEXTS).map(([key, ctx]) => (
                          <option key={key} value={key}>{ctx.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                        Scoring Impact
                      </label>
                      <select
                        value={question.aiWeight || 'medium'}
                        onChange={(e) => handleChange('aiWeight', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                          border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                          focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
                      >
                        {AI_WEIGHTS.map(w => (
                          <option key={w.value} value={w.value}>{w.label}</option>
                        ))}
                      </select>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default QuestionEditor;
